import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const FENCES_IMG =
  "https://images.unsplash.com/photo-1661161534610-d7c37163b8fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXJkZW4lMjBmZW5jaW5nJTIwbGFuZHNjYXBpbmclMjBwYXZpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fGdyZWVufDE3OTMzNzQ3NzJ8MA&ixlib=rb-4.1.0&q=85";
const GARDEN_IMG =
  "https://images.unsplash.com/photo-1711098256574-7b497260cdc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXJkZW4lMjB0ZXJyYWNlJTIwcGF0aW8lMjBzdG9uZSUyMHBhdmluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTMzNzQ3Nzh8MA&ixlib=rb-4.1.0&q=85";

const HIGHLIGHTS = [
  {
    title: "Zäune",
    image: FENCES_IMG,
    alt: "Moderner Zaun im Garten",
    testId: "service-card-fences",
    points: ["Doppelstabmatten & Gitterzäune", "WPC- und Holzzäune", "Sichtschutz nach Maß", "Tore & Pforten inklusive"],
  },
  {
    title: "Gartenarbeiten",
    image: GARDEN_IMG,
    alt: "Gepflegter Garten mit Terrasse",
    testId: "service-card-garden",
    points: ["Gartengestaltung & -pflege", "Heckenschnitt & Grünflächen", "Bepflanzung & Rasenflächen", "Saisonale Gartenarbeiten"],
  },
];

const EXTRAS = [
  {
    title: "Pflasterarbeiten",
    description:
      "Terrassen, Einfahrten und Gehwege aus Naturstein, Betonstein oder Klinker – eben, stabil & langlebig.",
    testId: "service-card-paving",
  },
  {
    title: "Erdarbeiten & Drainage",
    description:
      "Aushub, Planierung und fachgerechte Entwässerung – für trockene Mauern und dauerhaft stabile Flächen.",
    testId: "service-card-earthworks",
  },
];

export default function Services() {
  return (
    <section id="leistungen" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#244A32]">Unsere Leistungen</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Zäune & Garten – unsere Kernkompetenz
          </h2>
          <p className="mt-4 text-base text-[#575953] leading-relaxed">
            Qualität, die man sieht: Wir bauen Zäune, die Jahrzehnte halten, und pflegen Gärten,
            die jedes Jahr aufs Neue Freude bereiten – und kümmern uns um alles, was dazugehört.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {HIGHLIGHTS.map((s, i) => (
            <motion.article
              key={s.title}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="group bg-white rounded-2xl border border-[#E5E2DC] overflow-hidden hover:shadow-xl hover:shadow-[#244A32]/10 transition-shadow"
            >
              <div className="h-56 sm:h-64 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold">{s.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-[#575953]">
                      <Check className="w-4 h-4 mt-0.5 text-[#244A32] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#termin"
                  data-testid={`${s.testId}-cta`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#244A32] hover:gap-3 transition-all"
                >
                  Unverbindlich anfragen <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-6 lg:mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {EXTRAS.map((s, i) => (
            <motion.article
              key={s.title}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="bg-[#F1EFEA] rounded-2xl border border-[#E5E2DC] p-6 sm:p-8"
            >
              <h3 className="font-heading text-lg sm:text-xl font-semibold">{s.title}</h3>
              <p className="mt-3 text-sm text-[#575953] leading-relaxed">{s.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
